import { useLazyQuery, useQuery } from '@apollo/client'
import {
  Button,
  Collapse,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import ArrowRightIcon from '@material-ui/icons/ArrowRight'
import FilterListIcon from '@material-ui/icons/FilterList'
import Pagination from '@material-ui/lab/Pagination/Pagination'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import PageHeader from '../../components/PageHeader'
import SectionTitle from '../../components/SectionTitle'
import {
  AUDIT_LOGS_ACTION_NAMES_QUERY,
  AUDIT_LOGS_COUNT_QUERY,
  AUDIT_LOGS_QUERY
} from '../../graphql/auditLogs'
import {
  addDaysToDate,
  DateObject,
  getTodayDate,
  gregorianToJalaali
} from '../../helpers/date'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { ROLE } from '../../staticRoleManagement/types'
import { InputData, LogsData } from '../../types/auditLogs'
import AuditLogsFilters from './AuditLogsFilters'
import AuditLogsFormListDesktop from './AuditLogsFormList/AuditLogsFormListDesktop'
import useStyles from './styles/index.styles'

const AuditLogsForm = () => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [page, setPage] = useState(1)
  const [actionNameValue, setActionNameValue] = useState<any>(null)
  const [dateObject, setDateObject] = useState<DateObject>({
    gregorianStartDate: getTodayDate(),
    gregorianEndDate: addDaysToDate(getTodayDate(), 1),
    jalaaliStartDate: gregorianToJalaali(getTodayDate()),
    jalaaliEndDate: gregorianToJalaali(addDaysToDate(getTodayDate(), 1))
  })

  const [keywordsFieldValues, setKeywordsFieldValues] = useState<any>([])
  const [count, setCount] = useState<number>(1)
  const [perPage] = useState<number>(10)
  const [filterButtonSelected, setFilterButtonSelected] =
    useState<boolean>(false)
  const [logsCount, setLogsCount] = useState(0)
  const [newLogsCount, setNewLogsCount] = useState(0)
  const [inputData, setInputData] = useState<InputData>({
    perPage: perPage,
    from: dateObject.gregorianStartDate || '',
    keywords: [],
    action: '',
    userEmails: [],
    page: 1,
    requestID: '',
    to: dateObject.gregorianEndDate || '',
    entityCode: '',
    entityId: '',
    correlationId: '',
    userIP: '',
    accountType: '',
    connectionMethod: '',
    sort: 'DATE_DESC',
    status: undefined,
    integrityStatus: undefined
  })
  const [actionNames, setActionNames] = useState<
    { label: string; value: string }[]
  >([])
  const [getAuditLogsConut] = useLazyQuery<{
    auditLogsCount: {
      totalNumberOfLogs: number
    }
  }>(AUDIT_LOGS_COUNT_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (res) => {
      setNewLogsCount(res.auditLogsCount.totalNumberOfLogs)
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [
    getAuditLogsData,
    { data: auditLogsData, loading: loadingForAuditLogs }
  ] = useLazyQuery<{ auditLogs: LogsData }>(AUDIT_LOGS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
    onCompleted: (res) => {
      setLogsCount(res.auditLogs.totalNumberOfLogs)
      getAuditLogsConut({
        variables: {
          input: inputData
        }
      })
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const { loading: loadingForActionNames } = useQuery<{
    actionNames: { label: string; value: string }[]
  }>(AUDIT_LOGS_ACTION_NAMES_QUERY, {
    onCompleted: (res) => {
      const updatedActionNames = res.actionNames.map((an) => ({
        label: getTranslate(an.label),
        value: an.value
      }))
      setActionNames(updatedActionNames)
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  useEffect(() => {
    if (
      auditLogsData?.auditLogs.logs &&
      auditLogsData?.auditLogs.logs.length > 0 &&
      auditLogsData?.auditLogs.requestID
    )
      setInputData({
        ...inputData,
        requestID: auditLogsData.auditLogs.requestID
      })
  }, [auditLogsData?.auditLogs.requestID])

  useEffect(() => {
    if (auditLogsData?.auditLogs.totalNumberOfLogs) {
      setCount(
        Math.ceil(auditLogsData?.auditLogs.totalNumberOfLogs / perPage) || 1
      )
    }
  }, [auditLogsData?.auditLogs.totalNumberOfLogs])

  useEffect(() => {
    if (logsCount) {
      setFilterButtonSelected(false)
    } else {
      setFilterButtonSelected(true)
    }
  }, [logsCount])

  useEffect(() => {
    if (inputData.to) {
      getAuditLogsConut({
        variables: {
          input: inputData
        }
      })

      const interval = setInterval(() => {
        getAuditLogsConut({
          variables: {
            input: inputData
          }
        })
      }, 30000) // 30 seconds

      // Cleanup the interval on component unmount
      return () => clearInterval(interval)
    }
  }, [loadingForAuditLogs])

  useEffect(() => {
    if (inputData.to) {
      setInputData({
        ...inputData,
        page: page
      })
      getAuditLogsData({
        variables: {
          input: {
            ...inputData,
            page: page
          }
        }
      })
    }
  }, [page])

  useEffect(() => {
    setInputData({
      ...inputData,
      requestID: ''
    })
  }, [
    inputData.action,
    inputData.userEmails,
    inputData.entityId,
    inputData.from,
    inputData.to,
    inputData.keywords,
    inputData.correlationId,
    inputData.userIP,
    inputData.accountType,
    inputData.connectionMethod,
    inputData.sort,
    inputData.status,
    inputData.integrityStatus
  ])

  useEffect(() => {
    setInputData({
      ...inputData,
      from: dateObject.gregorianStartDate || '',
      to: dateObject.gregorianEndDate || ''
    })
  }, [dateObject])

  const isRoleAdmin = getRoleFromStorage() === ROLE.ADMIN

  return isSmallScreen ? (
    <Typography variant="h4">
      {getTranslate('Please visit this page on your Desktop')}
    </Typography>
  ) : (
    <div>
      <PageHeader title={getTranslate('Audit Logs')} />
      <div className={classes.header}>
        <SectionTitle title={getTranslate('Audit Logs')} />
        <Button
          size={'medium'}
          startIcon={<FilterListIcon />}
          endIcon={
            filterButtonSelected ? (
              <ArrowDropDownIcon />
            ) : (
              <ArrowRightIcon className={lang === 'fa' ? classes.rotate : ''} />
            )
          }
          variant="contained"
          color="primary"
          onClick={() => setFilterButtonSelected(!filterButtonSelected)}>
          {getTranslate('Filters')}
        </Button>
      </div>
      <Collapse in={filterButtonSelected}>
        <AuditLogsFilters
          actionNameValue={actionNameValue}
          setActionNameValue={setActionNameValue}
          dateObject={dateObject}
          setDateObject={setDateObject}
          keywordsFieldValues={keywordsFieldValues}
          setKeywordsFieldValues={setKeywordsFieldValues}
          getAuditLogsData={getAuditLogsData}
          loadingForAuditLogs={loadingForAuditLogs}
          perPage={perPage}
          setPage={setPage}
          setInputData={setInputData}
          inputData={inputData}
          actionNames={actionNames}
          loadingForActionNames={loadingForActionNames}
          isRoleAdmin={isRoleAdmin}
        />
      </Collapse>
      <AuditLogsFormListDesktop
        data={auditLogsData?.auditLogs}
        loadingForAuditLogs={loadingForAuditLogs}
        getAuditLogsData={getAuditLogsData}
        inputData={inputData}
        setInputData={setInputData}
        setPage={setPage}
        actionNames={actionNames}
        newLogsCount={logsCount ? newLogsCount - logsCount : 0}
        isRoleAdmin={isRoleAdmin}
      />
      {auditLogsData?.auditLogs &&
        auditLogsData?.auditLogs.totalNumberOfLogs > 0 && (
          <div className={classes.paginationContainer}>
            <Pagination
              page={page}
              count={count}
              color="primary"
              size={isSmallScreen ? 'small' : 'medium'}
              onChange={(_, value) => {
                setPage(value)
              }}
            />
          </div>
        )}
    </div>
  )
}
export default AuditLogsForm
